import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  TextField,
  Typography,
  InputAdornment,
  BoxProps,
  SelectProps,
  CircularProgress,
  Paper,
} from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox, Search } from '@mui/icons-material';
import { useState } from 'react';
import { MAX_MULTISELECT_OPTIONS } from '@common/types/constants';
import { theme } from '@store';

type NestedOption = { value: string; label: string; isMandatory?: boolean };

const NONE_OPTION_VALUE = 'null';

interface IDynamicFormFieldProps {
  name: string;
  label?: string;
  disabled?: boolean;
  optional?: boolean;
  placeholder?: string;
  handleChange: (values: string[]) => void;
  value: string[];
  options?: NestedOption[];
  clearable?: boolean;
  error?: boolean;
  minOptionMenuWidth?: string;
  startAdornment?: React.ReactNode;
  loading?: boolean;
  minSelectedOptionsCount?: number;
  showSelectedCount?: boolean;
  paperFooter?: React.ReactNode;
  selectedCountOverride?: number;
  onInputChange?: (value: string) => void;
  hideHelperText?: boolean;
}

export const MultiselectSearchableField = ({
  name,
  label,
  options = [],
  disabled = false,
  optional,
  handleChange,
  value,
  clearable = true,
  error,
  placeholder = 'Type to search',
  minOptionMenuWidth,
  sx,
  startAdornment,
  size = 'medium',
  loading = false,
  minSelectedOptionsCount = 0,
  showSelectedCount = false,
  paperFooter,
  selectedCountOverride,
  onInputChange,
  hideHelperText = false,
}: IDynamicFormFieldProps & Pick<BoxProps, 'sx'> & SelectProps) => {
  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const sxObject = sx && typeof sx === 'object' && !Array.isArray(sx) ? sx : {};
  const { maxHeight = '56px', ...restSx } = sxObject as { maxHeight?: number };

  const finalOptions = loading
    ? []
    : clearable
      ? [{ value: '', label: 'All' }, ...options]
      : options;

  const icon = <CheckBoxOutlineBlank fontSize='small' />;
  const checkedIcon = <CheckBox fontSize='small' />;
  const startAdornmentIcon = startAdornment ? startAdornment : <Search />;

  const isAllChecked = value.length === 0;

  return (
    <Box sx={{ width: '100%', ...restSx }}>
      <Autocomplete<NestedOption, true, true, false>
        slots={{
          ...(showSelectedCount || paperFooter
            ? {
                paper: props => (
                  <Paper {...props}>
                    {props.children}
                    <Box
                      onMouseDown={e => e.preventDefault()}
                      sx={{
                        borderTop: theme =>
                          `1px solid ${theme.palette.divider}`,
                        px: 2,
                        py: 1,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      {showSelectedCount && (
                        <Typography
                          variant='body2'
                          color={theme.palette.semiBlack.main}
                        >
                          {selectedCountOverride ?? value.length} out of{' '}
                          {options.length} selected
                        </Typography>
                      )}
                      {paperFooter}
                    </Box>
                  </Paper>
                ),
              }
            : {}),
        }}
        id={name}
        inputValue={inputValue}
        onInputChange={(_, newValue, reason) => {
          if (reason === 'reset') return;
          setInputValue(newValue);
          onInputChange?.(newValue);
        }}
        onKeyDown={event => {
          const target = event.target as HTMLInputElement;

          if (event.key === 'Backspace' && target.value === '') {
            event.defaultMuiPrevented = true;
          }
        }}
        filterOptions={(options, state) => {
          const search = state.inputValue.toLowerCase();
          return options.filter(o => o.label.toLowerCase().includes(search));
        }}
        getOptionDisabled={option => {
          if (option.value === '') return false;

          const alreadySelected = value.includes(option.value);
          if (
            value.includes(option.value) &&
            value.length <= minSelectedOptionsCount
          )
            return true;
          return value.length >= MAX_MULTISELECT_OPTIONS && !alreadySelected;
        }}
        size={size}
        multiple
        disableCloseOnSelect
        options={finalOptions}
        getOptionLabel={o => o.label}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        value={finalOptions.filter(o => value.includes(o.value))}
        slotProps={{
          ...(minOptionMenuWidth && {
            paper: {
              style: {
                minWidth: minOptionMenuWidth,
              },
            },
          }),
          ...(showSelectedCount && {
            listbox: {
              sx: {
                maxHeight: 300,
                overflowY: 'auto',
                py: 0,
              },
            },
          }),
        }}
        onChange={(_, newValue) => {
          const newValueList = newValue.map(o => o.value);
          if (
            !newValue.some(o => o.value === '') &&
            newValue.length <= minSelectedOptionsCount
          ) {
            handleChange(newValueList);
            return;
          }

          if (newValue.some(o => o.value === '')) {
            handleChange([]);
            return;
          }
          if (newValueList.includes(NONE_OPTION_VALUE)) {
            // "None" is exclusive: picking it clears the other options, and
            // picking any other option while it is selected clears it.
            handleChange(
              value.includes(NONE_OPTION_VALUE)
                ? newValueList.filter(v => v !== NONE_OPTION_VALUE)
                : [NONE_OPTION_VALUE],
            );
            return;
          }
          handleChange(newValueList);
        }}
        renderOption={(props, option, { selected, index }) => {
          const isAllOption = option.value === '';
          const { key, ...restProps } = props;

          return (
            <li key={`${key}-${index}`} {...restProps}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={isAllOption ? isAllChecked : selected}
                sx={{ mr: 1 }}
              />
              <Typography variant='body2'>{option.label}</Typography>
            </li>
          );
        }}
        renderInput={params => {
          const { slotProps: _sp, ...restParams } = params;
          return (
            <TextField
              {...restParams}
              sx={{
                '& .MuiInputBase-root': {
                  height: '100%',
                  ...(maxHeight && { maxHeight }),
                  pl: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  flexWrap: 'nowrap',
                },
              }}
              label={label}
              required={!optional}
              error={error}
              helperText={
                value.length >= MAX_MULTISELECT_OPTIONS && !hideHelperText
                  ? `Maximum of ${MAX_MULTISELECT_OPTIONS} items selected.`
                  : undefined
              }
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              slotProps={{
                input: {
                  ...params.slotProps.input,
                  startAdornment: (
                    <>
                      <InputAdornment position='start'>
                        {startAdornmentIcon}
                        {!loading && value.length > 0 && (
                          <Chip
                            label={value.length}
                            size='small'
                            sx={{
                              ml: 0.5,
                              mr: 0.5,
                              bgcolor: theme => theme.palette.primary.main,
                              color: theme => theme.palette.common.white,
                              '& .MuiChip-label': { fontSize: '0.75rem' },
                            }}
                          />
                        )}
                      </InputAdornment>
                    </>
                  ),
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color='inherit' size={20} />
                      ) : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
                htmlInput: {
                  ...params.slotProps.htmlInput,
                  placeholder: focused ? placeholder : '',
                  value:
                    isAllChecked && !focused
                      ? 'All'
                      : (params.slotProps.htmlInput?.value ?? ''),
                },
                inputLabel: {
                  shrink: focused || value.length > 0 || isAllChecked,
                },
              }}
            />
          );
        }}
        disabled={disabled}
        loading={loading}
        loadingText='Loading...'
      />
    </Box>
  );
};
